import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/AppLayout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Field } from "@/pages/Companies";
import { toast } from "sonner";
import { Plus, Minus, Archive, AlertTriangle, Pencil, Trash2, Loader2, Search } from "lucide-react";

const fmt = (n) => n == null ? "—" : new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" }).format(n);

const EMPTY = { name: "", sku: "", category: "", qty: 0, unit: "ea", cost_price: 0, markup_percent: 40, low_stock_threshold: 2, notes: "" };

export default function Stock() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null); // null | {form}
  const [q, setQ] = useState("");

  const { data } = useQuery({ queryKey: ["stock"], queryFn: async () => (await api.get("/stock")).data });
  const items = (data?.items || []).filter((i) =>
    !q || `${i.name} ${i.sku || ""} ${i.category || ""}`.toLowerCase().includes(q.toLowerCase()));
  const lowItems = (data?.items || []).filter((i) => i.low_stock);

  const invalidate = () => qc.invalidateQueries({ queryKey: ["stock"] });

  const saveMut = useMutation({
    mutationFn: async (form) => {
      const payload = {
        ...form,
        qty: parseFloat(form.qty) || 0,
        cost_price: parseFloat(form.cost_price) || 0,
        markup_percent: parseFloat(form.markup_percent) || 0,
        low_stock_threshold: parseFloat(form.low_stock_threshold) || 0,
      };
      if (form.id) return (await api.patch(`/stock/${form.id}`, payload)).data;
      return (await api.post("/stock", payload)).data;
    },
    onSuccess: () => { invalidate(); setEditing(null); toast.success("Stock item saved"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Save failed"),
  });
  const adjustMut = useMutation({
    mutationFn: async ({ id, delta }) => (await api.post(`/stock/${id}/adjust`, { delta })).data,
    onSuccess: (item) => {
      invalidate();
      if (item.low_stock) toast.warning(`LOW STOCK: ${item.name} — only ${item.qty} left`);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Adjust failed"),
  });
  const deleteMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/stock/${id}`)).data,
    onSuccess: () => { invalidate(); toast.success("Item deleted"); },
  });

  const f = editing || {};
  const sellPreview = (parseFloat(f.cost_price) || 0) * (1 + (parseFloat(f.markup_percent) || 0) / 100);

  return (
    <div>
      <PageHeader
        title="Stock Take"
        subtitle="Product inventory — cost price + markup, with low-stock alerts."
        breadcrumb={["Operations", "Stock Take"]}
        actions={
          <Button data-testid="stock-add-btn" onClick={() => setEditing({ ...EMPTY })} className="rounded-sm h-10 bg-cyan-600 hover:bg-cyan-700 text-white">
            <Plus size={16} className="mr-2" /> Add stock item
          </Button>
        }
      />
      <div className="px-4 sm:px-6 lg:px-10 py-6 space-y-4">
        {lowItems.length > 0 && (
          <Card className="rounded-sm border-amber-300 bg-amber-50 p-4" data-testid="stock-low-banner">
            <div className="flex items-start gap-3">
              <AlertTriangle size={20} className="text-amber-600 shrink-0 mt-0.5" />
              <div className="text-sm">
                <div className="font-display font-semibold text-amber-900">{lowItems.length} item{lowItems.length > 1 ? "s" : ""} running low — reorder needed</div>
                <div className="text-xs text-amber-800 mt-1 flex flex-wrap gap-x-4 gap-y-1">
                  {lowItems.map((i) => <span key={i.id}><b>{i.name}</b>: {i.qty} {i.unit} left</span>)}
                </div>
              </div>
            </div>
          </Card>
        )}

        <div className="relative max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="stock-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search stock…" className="pl-9 h-10 rounded-sm" />
        </div>

        <Card className="rounded-sm border-slate-200 industrial-shadow overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left stamp-label text-slate-500">
                <th className="px-4 py-3">Item</th>
                <th className="px-4 py-3 hidden sm:table-cell">SKU</th>
                <th className="px-4 py-3 text-center">Qty</th>
                <th className="px-4 py-3 text-right">Our cost</th>
                <th className="px-4 py-3 text-right hidden sm:table-cell">Markup</th>
                <th className="px-4 py-3 text-right">Sell price</th>
                <th className="px-4 py-3 text-right"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((i) => (
                <tr key={i.id} className={i.low_stock ? "bg-amber-50/60" : "hover:bg-cyan-50/30"} data-testid={`stock-row-${i.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-900 flex items-center gap-2">
                      {i.name}
                      {i.low_stock && <Badge variant="outline" className="rounded-sm text-[9px] border-amber-400 bg-amber-100 text-amber-800" data-testid={`stock-low-badge-${i.id}`}>LOW STOCK</Badge>}
                    </div>
                    {i.category && <div className="text-[10px] text-slate-500">{i.category}</div>}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-500 hidden sm:table-cell">{i.sku || "—"}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center gap-1.5">
                      <Button size="sm" variant="outline" data-testid={`stock-minus-${i.id}`} onClick={() => adjustMut.mutate({ id: i.id, delta: -1 })} className="h-7 w-7 p-0 rounded-sm"><Minus size={12} /></Button>
                      <span className={`font-mono font-semibold w-12 text-center ${i.low_stock ? "text-amber-700" : "text-slate-900"}`} data-testid={`stock-qty-${i.id}`}>{i.qty}</span>
                      <Button size="sm" variant="outline" data-testid={`stock-plus-${i.id}`} onClick={() => adjustMut.mutate({ id: i.id, delta: 1 })} className="h-7 w-7 p-0 rounded-sm"><Plus size={12} /></Button>
                    </div>
                    <div className="text-center text-[9px] text-slate-400 mt-0.5">{i.unit} · alert ≤ {i.low_stock_threshold}</div>
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-slate-700">{fmt(i.cost_price)}</td>
                  <td className="px-4 py-3 text-right font-mono text-slate-500 hidden sm:table-cell">{i.markup_percent}%</td>
                  <td className="px-4 py-3 text-right font-mono font-semibold text-cyan-600">{fmt(i.sell_price)}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <Button size="sm" variant="ghost" data-testid={`stock-edit-${i.id}`} onClick={() => setEditing({ ...EMPTY, ...i })} className="h-8 w-8 p-0 text-slate-400 hover:text-cyan-600"><Pencil size={14} /></Button>
                    <Button size="sm" variant="ghost" data-testid={`stock-delete-${i.id}`} onClick={() => { if (window.confirm(`Delete "${i.name}" from stock?`)) deleteMut.mutate(i.id); }} className="h-8 w-8 p-0 text-slate-400 hover:text-rose-600"><Trash2 size={14} /></Button>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-16 text-center text-slate-400">
                  <Archive size={28} className="mx-auto mb-2 text-slate-300" />
                  No stock items yet. <button className="text-cyan-600 font-semibold hover:underline" onClick={() => setEditing({ ...EMPTY })}>Add the first one →</button>
                </td></tr>
              )}
            </tbody>
          </table>
        </Card>
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="rounded-sm sm:max-w-lg" data-testid="stock-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">{f.id ? "Edit stock item" : "Add stock item"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Field label="Item name" required><Input data-testid="stock-form-name" value={f.name || ""} onChange={(e) => setEditing({ ...f, name: e.target.value })} placeholder="e.g. Colorbond panel 2.4m Monument" /></Field></div>
            <Field label="SKU"><Input data-testid="stock-form-sku" value={f.sku || ""} onChange={(e) => setEditing({ ...f, sku: e.target.value })} /></Field>
            <Field label="Category"><Input value={f.category || ""} onChange={(e) => setEditing({ ...f, category: e.target.value })} placeholder="e.g. Panels" /></Field>
            <Field label="Qty on hand"><Input data-testid="stock-form-qty" type="number" min="0" value={f.qty} onChange={(e) => setEditing({ ...f, qty: e.target.value })} /></Field>
            <Field label="Unit"><Input value={f.unit || "ea"} onChange={(e) => setEditing({ ...f, unit: e.target.value })} /></Field>
            <Field label="Our cost ($)"><Input data-testid="stock-form-cost" type="number" min="0" step="0.01" value={f.cost_price} onChange={(e) => setEditing({ ...f, cost_price: e.target.value })} /></Field>
            <Field label="Markup (%)"><Input data-testid="stock-form-markup" type="number" min="0" value={f.markup_percent} onChange={(e) => setEditing({ ...f, markup_percent: e.target.value })} /></Field>
            <Field label="Low-stock alert at (qty)"><Input data-testid="stock-form-threshold" type="number" min="0" value={f.low_stock_threshold} onChange={(e) => setEditing({ ...f, low_stock_threshold: e.target.value })} /></Field>
            <div className="flex flex-col justify-end pb-1">
              <div className="text-[10px] stamp-label text-slate-500">Sell price (auto)</div>
              <div className="font-mono font-bold text-lg text-cyan-600" data-testid="stock-form-sell-preview">{fmt(sellPreview)}</div>
            </div>
            <div className="col-span-2"><Field label="Notes"><Textarea rows={2} value={f.notes || ""} onChange={(e) => setEditing({ ...f, notes: e.target.value })} /></Field></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} className="rounded-sm">Cancel</Button>
            <Button data-testid="stock-form-save-btn" onClick={() => { if (!f.name) { toast.error("Item name required"); return; } saveMut.mutate(f); }} disabled={saveMut.isPending} className="rounded-sm bg-cyan-600 hover:bg-cyan-700">
              {saveMut.isPending ? <Loader2 size={14} className="animate-spin mr-1.5" /> : null} Save item
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
